import { useCallback, useState } from 'react';
import {
  fetchHorariosDisponiveisSala,
  fetchBloqueios,
} from '../services/api.ts';
import {
  AvailableTimeOption,
  formatDateKey,
  collectBlockedSets,
  buildAvailableTimeOptions,
  pickInitialTimeOption,
} from '../components/admin/reservationForm.utils.ts';

export type LoadShiftsOptions = {
  date?: Date;
  keepCurrentSelection?: boolean;
  preselectedHorarioId?: number | string | null;
  preselectedTime?: string | null;
};

export type FormTimeFields = {
  turno: number | undefined;
  horario_id: number | string | null;
  time: string;
};

type UseReservationFormShiftsArgs = {
  formDate: Date;
  onApplyTimeFields: (fields: FormTimeFields) => void;
};

export default function useReservationFormShifts({
  formDate,
  onApplyTimeFields,
}: UseReservationFormShiftsArgs) {
  const [availableTimeOptions, setAvailableTimeOptions] = useState<AvailableTimeOption[]>([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [noAvailableShiftsMessage, setNoAvailableShiftsMessage] = useState('');
  const [blockedTurnos, setBlockedTurnos] = useState<number[]>([]);
  const [blockedHorarioIds, setBlockedHorarioIds] = useState<number[]>([]);
  const [blockedShiftNotice, setBlockedShiftNotice] = useState('');

  const clearShiftState = useCallback(() => {
    setAvailableTimeOptions([]);
    setNoAvailableShiftsMessage('');
    setBlockedTurnos([]);
    setBlockedHorarioIds([]);
    setBlockedShiftNotice('');
  }, []);

  const loadAvailableShiftsAndTimes = useCallback(async (
    salaId: string,
    options: LoadShiftsOptions = {}
  ) => {
    if (!salaId) {
      clearShiftState();
      return {};
    }

    const {
      date,
      keepCurrentSelection = false,
      preselectedHorarioId,
      preselectedTime,
    } = options;

    try {
      setLoadingShifts(true);
      setNoAvailableShiftsMessage('');

      const referenceDate = date ?? formDate;
      const formattedDate = formatDateKey(referenceDate);
      const shiftsData = await fetchHorariosDisponiveisSala(salaId, formattedDate);
      const shiftMap = { ...(shiftsData || {}) };
      const shiftKeys = Object.keys(shiftMap);

      let blockedTurnoSet = new Set<number>();
      let blockedHorarioSet = new Set<number>();

      try {
        const bloqueios = await fetchBloqueios();
        const blocked = collectBlockedSets(bloqueios, formattedDate, salaId);
        blockedTurnoSet = blocked.blockedTurnoSet;
        blockedHorarioSet = blocked.blockedHorarioSet;
      } catch (error) {
        console.error('Erro ao verificar bloqueios:', error);
      }

      setBlockedTurnos(Array.from(blockedTurnoSet));
      setBlockedHorarioIds(Array.from(blockedHorarioSet));
      setBlockedShiftNotice('');

      const normalizedTimeOptions = buildAvailableTimeOptions(
        shiftMap,
        blockedTurnoSet,
        blockedHorarioSet
      );

      setAvailableTimeOptions(normalizedTimeOptions);

      if (shiftKeys.length === 0 || normalizedTimeOptions.length === 0) {
        setNoAvailableShiftsMessage('Nenhum horário configurado para esta data.');
        setBlockedShiftNotice('');
        setAvailableTimeOptions([]);
        if (!keepCurrentSelection) {
          onApplyTimeFields({
            turno: undefined,
            horario_id: null,
            time: '',
          });
        }
        return shiftMap;
      }

      setNoAvailableShiftsMessage('');

      const selectedOption = pickInitialTimeOption(normalizedTimeOptions, {
        keepCurrentSelection,
        preselectedTime,
        preselectedHorarioId,
      });

      onApplyTimeFields({
        turno: selectedOption?.turno ?? undefined,
        horario_id: selectedOption?.horario_id ?? null,
        time: selectedOption?.time ?? '',
      });

      return shiftMap;
    } catch (error) {
      console.error('Erro ao carregar horarios disponiveis:', error);
      setAvailableTimeOptions([]);
      setNoAvailableShiftsMessage('Não foi possível carregar horários disponíveis.');
      setBlockedTurnos([]);
      setBlockedHorarioIds([]);
      setBlockedShiftNotice('');
      return {};
    } finally {
      setLoadingShifts(false);
    }
  }, [clearShiftState, formDate, onApplyTimeFields]);

  const resolveSelectedTimeOption = useCallback((value: string): AvailableTimeOption | null => {
    if (!value) return null;
    return availableTimeOptions.find((option) => option.value === value) || null;
  }, [availableTimeOptions]);

  return {
    availableTimeOptions,
    setAvailableTimeOptions,
    loadingShifts,
    noAvailableShiftsMessage,
    blockedTurnos,
    blockedHorarioIds,
    blockedShiftNotice,
    setBlockedShiftNotice,
    clearShiftState,
    loadAvailableShiftsAndTimes,
    resolveSelectedTimeOption,
  };
}
